/**
 * Snack Media's in-page ad slots (FFH-3481). The wrapper fills any element
 * whose id matches a unit in our Pubstack config: exactly for a numbered slot,
 * by prefix for a repeater, so repeaters take a -N suffix to keep ids unique.
 *
 * Reserved heights are the tallest size each unit accepts in that config
 * (mobile.json and desktop.json under cdn-adm.pbstck.com/config for tag
 * ff68a2df, read on 2 November 2026), so an ad arriving never moves the page.
 * The exception is a 300x600 on mobile, which the wrapper shows through a
 * window that scrolls past it ("parallax"), so it needs only the window.
 * The wrapper picks the mobile or desktop units once, by window width, from
 * 768px up for desktop; styles/globals.css switches heights at the same width.
 */

type NumberedIncontentSlotId =
  | 'snack-incontent-1'
  | 'snack-incontent-2'
  | 'snack-incontent-3';

export type IncontentSlotId =
  | NumberedIncontentSlotId
  | `snack-incontent-repeater-${number}`;

export const SIDEBAR_SLOT_ID = 'snack-sidebar-1';

export type SnackSlotId = IncontentSlotId | typeof SIDEBAR_SLOT_ID;

export interface ReservedHeights {
  mobileInPx: number;
  desktopInPx: number;
}

const NUMBERED_INCONTENT_SLOT_COUNT = 3;

const FIRST_INCONTENT_SLOT_ID: IncontentSlotId = 'snack-incontent-1';

// 336x280 on both. The first unit is the only in-content one without 300x600.
const FIRST_INCONTENT_HEIGHTS: ReservedHeights = {
  mobileInPx: 280,
  desktopInPx: 280
};

// A 300x600 through the 310px parallax window Snack's doc gives on mobile, and
// 336x280 on desktop.
const LATER_INCONTENT_HEIGHTS: ReservedHeights = {
  mobileInPx: 310,
  desktopInPx: 280
};

// Desktop only. The rail's fixture ticker and tools sit below it, so it holds
// the tallest size it takes, 300x600, for them never to move.
const SIDEBAR_HEIGHTS: ReservedHeights = {
  mobileInPx: 0,
  desktopInPx: 600
};

/**
 * Placement for article bodies, as Snack suggested on 3 November 2026
 * (FFH-3481, FFH-3483): the video player after the second paragraph, the
 * first ad slot after the fourth, then one every five paragraphs.
 */
export const VIDEO_AFTER_PARAGRAPH = 2;
export const FIRST_SLOT_AFTER_PARAGRAPH = 4;
export const PARAGRAPHS_BETWEEN_SLOTS = 5;

/** Our own id for the video player's place in a body; not one of Snack's. */
export const VIDEO_SLOT_ID = 'snack-video';

/** The id of the nth in-content slot on a page, counting from 1. */
export const getIncontentSlotId = (position: number): IncontentSlotId =>
  position <= NUMBERED_INCONTENT_SLOT_COUNT
    ? (`snack-incontent-${position}` as NumberedIncontentSlotId)
    : `snack-incontent-repeater-${position - NUMBERED_INCONTENT_SLOT_COUNT}`;

export const getReservedHeights = (slotId: SnackSlotId): ReservedHeights => {
  if (slotId === SIDEBAR_SLOT_ID) return SIDEBAR_HEIGHTS;
  if (slotId === FIRST_INCONTENT_SLOT_ID) return FIRST_INCONTENT_HEIGHTS;
  return LATER_INCONTENT_HEIGHTS;
};

/**
 * What a block in an article body is, as far as ad placement cares: a
 * paragraph with words in it, other text (a heading, list item, quote or empty
 * paragraph), or anything else (an image, embed or data block).
 */
export type BodyBlockKind = 'paragraph' | 'text' | 'other';

export type PlannedSlotId = IncontentSlotId | typeof VIDEO_SLOT_ID;

export interface PlannedSlot {
  /** The index of the block the slot goes straight after. */
  afterIndex: number;
  slotId: PlannedSlotId;
}

/**
 * Where in-content slots go in an article body, given what each block is.
 *
 * A slot only ever follows a paragraph, and only when more text comes straight
 * after it, so an ad never ends the body or sits against an image, embed or
 * data block. When that rules a spot out, the slot moves to the next paragraph
 * that qualifies. It works on block kinds alone so that both article pages,
 * Sanity and legacy WordPress, can share it.
 *
 * With `shouldPlaceVideo`, the video player follows the same rules once, and
 * an ad slot due at the same paragraph waits for the next one that qualifies.
 */
export const planIncontentSlots = (
  kinds: readonly BodyBlockKind[],
  { shouldPlaceVideo = false }: { shouldPlaceVideo?: boolean } = {}
): PlannedSlot[] => {
  const slots: PlannedSlot[] = [];
  let adSlotCount = 0;
  let paragraphCount = 0;
  let nextSlotParagraph = FIRST_SLOT_AFTER_PARAGRAPH;
  let isVideoPending = shouldPlaceVideo;

  kinds.forEach((kind, index) => {
    if (kind !== 'paragraph') return;
    paragraphCount += 1;

    const nextKind = kinds[index + 1];
    if (nextKind !== 'paragraph' && nextKind !== 'text') return;

    if (isVideoPending && paragraphCount >= VIDEO_AFTER_PARAGRAPH) {
      slots.push({ afterIndex: index, slotId: VIDEO_SLOT_ID });
      isVideoPending = false;
      return;
    }

    if (paragraphCount >= nextSlotParagraph) {
      adSlotCount += 1;
      slots.push({
        afterIndex: index,
        slotId: getIncontentSlotId(adSlotCount)
      });
      nextSlotParagraph = paragraphCount + PARAGRAPHS_BETWEEN_SLOTS;
    }
  });

  return slots;
};
